import React, { useCallback, useMemo, useId, useState, useEffect } from 'react';
import { InputAdornment, TextField } from '@mui/material';
import Label from './Label';
import { ILabel } from '../constants/interfaces';
import {
  getBaseTextFieldStyles,
  getAccessibilityProps,
  getFormHelperTextSlotProps,
  getFieldName,
  getComputedFieldValues,
  inputLimits,
} from '../styles/fieldStyles';

/**
 * Props for the CurrencyField component
 */
export interface ICurrencyFieldProps {
  /** Current currency value as string */
  value: string;
  /** Callback fired when the currency value changes */
  onChange: (value: string) => void;
  /** Field configuration containing label text, validation rules, etc. */
  field: ILabel;
  /** Error message to display */
  errorText?: string;
  /** Whether to hide the label */
  isLabelHidden?: boolean;
  /** Whether the field is read-only/display only */
  displayOnly?: boolean;
  /** Width of the currency input */
  width?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is read-only */
  readOnly?: boolean;
  /** Custom placeholder text */
  placeholder?: string;
  /** Currency symbol to display */
  currencySymbol?: string;
  /** Number of decimal places for currency */
  decimalPlaces?: number;
  /** Minimum allowed value */
  min?: number;
  /** Maximum allowed value */
  max?: number;
  /** Maximum character length */
  maxLength?: number;
  /** Whether to show formatted value on blur */
  formatOnBlur?: boolean;
  /** Callback fired when input gains focus */
  onFocus?: () => void;
  /** Callback fired when input loses focus */
  onBlur?: () => void;
}

/**
 * Utility functions for currency input handling
 */
const currencyUtils = {
  /**
   * Formats currency value for display
   */
  formatCurrency: (value: string, decimalPlaces = 2): string => {
    if (!value || isNaN(Number(value))) {
      return value;
    }

    const num = Number(value);
    return num.toLocaleString('en-US', {
      maximumFractionDigits: decimalPlaces,
      minimumFractionDigits: decimalPlaces,
    });
  },

  /**
   * Checks if currency value is within bounds
   */
  isWithinBounds: (value: string, min?: number, max?: number): boolean => {
    if (!value) return true;

    const num = Number(value);
    if (isNaN(num)) return false;

    if (min !== undefined && num < min) return false;
    if (max !== undefined && num > max) return false;

    return true;
  },

  /**
   * Sanitizes currency input to allow only numbers and decimal point
   */
  sanitizeCurrencyInput: (value: string, decimalPlaces = 2): string => {
    // Remove all non-numeric characters except decimal point
    let sanitized = value.replace(/[^0-9.]/g, '');

    // Prevent more than one decimal point
    const parts = sanitized.split('.');
    if (parts.length > 2) {
      sanitized = parts[0] + '.' + parts.slice(1).join('');
    }

    // Limit decimal places
    if (parts.length === 2 && parts[1].length > decimalPlaces) {
      sanitized = parts[0] + '.' + parts[1].slice(0, decimalPlaces);
    }

    // Prevent leading zeros unless immediately followed by a dot
    if (/^0[0-9]+/.test(sanitized)) {
      sanitized = sanitized.replace(/^0+/, '0');
    }

    return sanitized;
  },

  /**
   * Formats a number as currency for display
   */
  toCurrencyString: (
    amount: number,
    symbol = '$',
    decimalPlaces = 2
  ): string => {
    return `${symbol}${amount.toLocaleString('en-US', {
      maximumFractionDigits: decimalPlaces,
      minimumFractionDigits: decimalPlaces,
    })}`;
  },

  /**
   * Removes formatting from currency value for editing
   */
  unformatCurrency: (value: string): string => {
    return value.replace(/[^0-9.]/g, '');
  },

  /**
   * Validates currency value based on field requirements
   */
  validateCurrency: (
    value: string,
    field: ILabel,
    min?: number,
    max?: number
  ): string | null => {
    if (field.isMandatory && !value.trim()) {
      return `${field.text} is required`;
    }

    if (value && isNaN(Number(value))) {
      return `${field.text} must be a valid amount`;
    }

    const numValue = Number(value);
    if (min !== undefined && numValue < min) {
      return `${field.text} must be at least $${min.toFixed(2)}`;
    }

    if (max !== undefined && numValue > max) {
      return `${field.text} must be at most $${max.toFixed(2)}`;
    }

    return null;
  },
};

/**
 * CurrencyField Component
 *
 * A reusable currency input component that integrates with form validation
 * and provides a consistent interface for monetary value input across the application.
 *
 * Features:
 * - Automatic currency formatting on blur
 * - Raw numeric input during editing
 * - Configurable decimal places
 * - Min/max value validation
 * - Currency symbol display
 * - Accessibility support with labels and ARIA attributes
 * - Consistent styling with the design system
 * - Mobile-optimized decimal keyboards
 * - Memoized performance optimizations
 *
 * @example
 * ```tsx
 * <CurrencyField
 *   value={price}
 *   onChange={handlePriceChange}
 *   field={priceField}
 *   errorText={validationError}
 *   currencySymbol="$"
 *   min={0}
 *   max={999999}
 * />
 * ```
 */
function CurrencyField(props: ICurrencyFieldProps) {
  const {
    value,
    onChange,
    field,
    errorText,
    isLabelHidden = false,
    displayOnly = false,
    width,
    disabled = false,
    readOnly = false,
    placeholder,
    currencySymbol = '$',
    decimalPlaces = 2,
    min,
    max,
    maxLength = inputLimits.maxLength.default,
    formatOnBlur = true,
    onFocus,
    onBlur,
  } = props;

  // Generate unique ID for accessibility
  const fieldId = useId();
  const labelId = `${fieldId}-label`;
  const errorId = `${fieldId}-error`;

  // Local state for display value (formatted vs unformatted)
  const [displayValue, setDisplayValue] = useState(value);
  const [, setIsFocused] = useState(false);

  // Sync display value with prop value
  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  // Memoize computed values using centralized utility plus specific values
  const computedValues = useMemo(
    () => ({
      ...getComputedFieldValues(
        errorText,
        disabled,
        displayOnly,
        width,
        maxLength
      ),
      isWithinBounds: currencyUtils.isWithinBounds(value, min, max),
    }),
    [errorText, disabled, displayOnly, width, maxLength, value, min, max]
  );

  // Optimized change handler with useCallback
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      const sanitizedValue = currencyUtils.sanitizeCurrencyInput(
        newValue,
        decimalPlaces
      );

      setDisplayValue(sanitizedValue);
      onChange(sanitizedValue);
    },
    [onChange, decimalPlaces]
  );

  // Optimized focus handler
  const handleFocus = useCallback(() => {
    setIsFocused(true);

    // Remove formatting for editing if formatOnBlur is enabled
    if (formatOnBlur && displayValue) {
      const unformatted = currencyUtils.unformatCurrency(displayValue);
      setDisplayValue(unformatted);
    }

    onFocus?.();
  }, [formatOnBlur, displayValue, onFocus]);

  // Optimized blur handler
  const handleBlur = useCallback(() => {
    setIsFocused(false);

    // Format currency value on blur if enabled
    if (formatOnBlur && displayValue && !isNaN(Number(displayValue))) {
      const formatted = currencyUtils.formatCurrency(
        displayValue,
        decimalPlaces
      );
      setDisplayValue(formatted);
    }

    onBlur?.();
  }, [formatOnBlur, displayValue, decimalPlaces, onBlur]);

  return (
    <>
      {/* Label with proper accessibility attributes */}
      {!isLabelHidden && (
        <Label
          text={field.text}
          required={field.isMandatory}
          htmlFor={fieldId}
        />
      )}

      {/* Currency input with enhanced props */}
      <TextField
        id={fieldId}
        fullWidth={String(computedValues.fieldWidth) === '100%'}
        name={getFieldName(field)}
        error={computedValues.hasError}
        size="small"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={computedValues.isDisabled}
        helperText={computedValues.helperText}
        type="text"
        placeholder={placeholder}
        sx={getBaseTextFieldStyles(
          typeof computedValues.fieldWidth === 'number'
            ? String(computedValues.fieldWidth)
            : computedValues.fieldWidth
        )}
        inputProps={{
          inputMode: 'decimal',
          max: max,
          maxLength: computedValues.effectiveMaxLength,
          min: min,
          ...getAccessibilityProps(
            fieldId,
            labelId,
            errorId,
            field,
            isLabelHidden,
            computedValues.hasError
          ),
          readOnly: readOnly,
        }}
        slotProps={{
          ...getFormHelperTextSlotProps(computedValues.hasError, errorId),
          input: {
            startAdornment: (
              <InputAdornment position="start">{currencySymbol}</InputAdornment>
            ),
          },
        }}
      />
    </>
  );
}

export default CurrencyField;

// Export utilities for testing and external use
export { currencyUtils };
